import type { ReactNode } from 'react';
import { AlertCircle, MessageCircle, MessageSquare, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { UpdatesSection } from '@/hooks/useOrderUpdates';
import { TimelineRow } from './TimelineRows';
import { baghdadDayKey, type TimelineDay } from './timeline';
import type { UpdatesTimeline } from './useUpdatesTimeline';
import { WhatsAppChat } from './WhatsAppChat';
import { formatUpdateDay, formatUpdateDayShort, localDigits } from './updatesFormat';

const ICON = 'h-5 w-5';

function DownNotice({ section }: { section: UpdatesSection }) {
  const { t } = useTranslation();
  const text =
    section === 'calls'
      ? String(t('updates_calls_unavailable'))
      : section === 'comments'
        ? String(t('updates_comments_unavailable'))
        : String(t('updates_whatsapp_unavailable'));
  return (
    <p
      role='alert'
      className='m-0 flex items-center gap-2 rounded-lg bg-[hsla(35,85%,52%,0.10)] px-3 py-2 text-[13px] text-[hsl(35,75%,32%)]'
    >
      <AlertCircle className='h-4 w-4 shrink-0' strokeWidth={2} aria-hidden />
      {text}
    </p>
  );
}

function EmptyState({ icon, title, hint }: { icon: ReactNode; title: string; hint?: string }) {
  return (
    <div className='flex flex-col items-center gap-2 px-4 py-10 text-center'>
      <span
        aria-hidden
        className='flex h-12 w-12 items-center justify-center rounded-full bg-sd-muted text-sd-muted-foreground'
      >
        {icon}
      </span>
      <p className='m-0 text-[14px] font-semibold text-sd-foreground'>{title}</p>
      {hint ? <p className='m-0 max-w-sm text-[13px] text-sd-muted-foreground'>{hint}</p> : null}
    </div>
  );
}

function DayGroup({ day, thisYear }: { day: TimelineDay; thisYear: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const label =
    day.relative === 'today'
      ? String(t('updates_day_today'))
      : day.relative === 'yesterday'
        ? String(t('updates_day_yesterday'))
        : day.key.startsWith(thisYear)
          ? formatUpdateDayShort(day.at, lang)
          : formatUpdateDay(day.at, lang);
  return (
    <div className='space-y-1'>
      <div className='flex items-center gap-3 px-2 pt-2'>
        <h3 className='m-0 text-[11px] font-semibold uppercase tracking-wide text-sd-muted-foreground'>
          {label}
        </h3>
        <span aria-hidden className='h-px flex-1 bg-sd-border' />
      </div>
      <ol className="relative m-0 list-none p-0 before:absolute before:bottom-6 before:start-[21px] before:top-6 before:w-[2px] before:rounded-full before:bg-sd-border before:content-['']">
        {day.items.map((item) => (
          <TimelineRow key={item.key} item={item} />
        ))}
      </ol>
    </div>
  );
}

export function TimelineSkeleton() {
  return (
    <div className='space-y-4 px-2' aria-busy='true'>
      {[0, 1, 2].map((i) => (
        <div key={i} className='flex items-center gap-3'>
          <div className='h-7 w-7 animate-pulse rounded-full bg-sd-muted' />
          <div className='flex-1 space-y-2'>
            <div className='h-3 w-1/3 animate-pulse rounded bg-sd-muted' />
            <div className='h-3 w-2/3 animate-pulse rounded bg-sd-muted' />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Below the chips: down notices, then the timeline, the WhatsApp chat, or an empty state. */
export function UpdatesTimelineView({ timeline }: { timeline: UpdatesTimeline }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const { filter, down, days, counts, earlierCount, truncated } = timeline;
  const thisYear = baghdadDayKey(Date.now()).slice(0, 4);

  if (filter !== 'all' && down.includes(filter)) return <DownNotice section={filter} />;

  if (filter === 'whatsapp') {
    return counts.whatsapp ? (
      <WhatsAppChat messages={timeline.whatsapp} />
    ) : (
      <EmptyState
        icon={<MessageCircle className={ICON} />}
        title={String(t('updates_no_whatsapp_yet'))}
      />
    );
  }

  const empty =
    filter === 'calls' ? (
      <EmptyState icon={<Phone className={ICON} />} title={String(t('updates_no_calls_yet'))} />
    ) : filter === 'comments' ? (
      <EmptyState
        icon={<MessageSquare className={ICON} />}
        title={String(t('updates_no_comments_yet'))}
      />
    ) : down.length ? null : (
      <EmptyState
        icon={<Phone className={ICON} />}
        title={String(t('updates_empty_title'))}
        hint={String(t('updates_empty_hint'))}
      />
    );

  return (
    <div className='space-y-3'>
      {filter === 'all'
        ? down.map((section) => <DownNotice key={section} section={section} />)
        : null}
      {days.length
        ? days.map((day) => <DayGroup key={day.key} day={day} thisYear={thisYear} />)
        : empty}
      {earlierCount > 0 ? (
        <div className='flex justify-center pt-1'>
          <button
            type='button'
            onClick={timeline.showEarlier}
            className='cursor-pointer rounded-full border border-solid border-sd-border bg-sd-card px-4 py-1.5 text-[13px] font-medium text-sd-foreground transition-colors hover:bg-sd-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring'
          >
            {localDigits(String(t('updates_show_earlier', { count: earlierCount })), lang)}
          </button>
        </div>
      ) : truncated && filter !== 'comments' && counts.calls ? (
        <p className='m-0 text-center text-[12px] text-sd-muted-foreground'>
          {String(t('updates_calls_truncated'))}
        </p>
      ) : null}
    </div>
  );
}
